import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useSubmission } from 'api/submission';
import { APPLICATION_STEPS } from 'common/constants';
import { LAUNCH_DARKLY_FLAGS } from 'common/launchDarklyFlags';
import { PrimaryButton } from 'components/lib/PrimaryButton';
import { TertiaryButton } from 'components/lib/TertiaryButton';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { useMemo, type FC } from 'react';
import { useParams } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import type { StepFooterActionsProps } from './StepFooterActions.types';

const StepFooterActions: FC<StepFooterActionsProps> = ({
  onNext,
  onPrevious,
  disableActions = false,
  nextBtnText = 'Next',
}) => {
  const themeHook = useTheme();
  const isMobileLayout = useMediaQuery(themeHook.breakpoints.down('sm'));
  const { id: submissionId } = useParams() as { id: string };
  const { data: submission } = useSubmission(submissionId);

  const isVehicleStepDisabledForNY = useFeatureFlags([
    {
      flagKey: LAUNCH_DARKLY_FLAGS['disable-vehicle-for-ny'],
      defaultValue: true,
    },
  ])[0] as boolean;

  const stepsSkipConditions = useMemo(
    () => ({
      [APPLICATION_STEPS.vehicles.key]: submission?.us_state === 'NY' && isVehicleStepDisabledForNY,
    }),
    [submission?.us_state, isVehicleStepDisabledForNY],
  );

  const {
    currentStep,
    actions: { updateStepper },
  } = useAppSelector('quoteStepper', ['currentStep', 'actions']);

  const handleNext = async () => {
    let canGoNext = true;

    if (onNext) {
      canGoNext = await onNext();
    }

    if (canGoNext) {
      updateStepper({ destination: 'next', submissionId, stepsSkipConditions });
    }
  };

  const handlePrevious = async () => {
    let canGoPrevious = true;

    if (onPrevious) {
      canGoPrevious = await onPrevious();
    }

    if (canGoPrevious) {
      updateStepper({ destination: 'previous', submissionId, stepsSkipConditions });
    }
  };

  return (
    <Box sx={isMobileLayout ? { mt: 'auto' } : {}}>
      <Box sx={{ mt: 4.5 }}>
        <PrimaryButton
          text={nextBtnText}
          onClick={handleNext}
          disabled={disableActions}
          data-test="Next"
        />
        {currentStep !== 0 && (
          <TertiaryButton
            text="Previous"
            onClick={handlePrevious}
            disabled={disableActions}
            data-test="Previous"
          />
        )}
      </Box>
    </Box>
  );
};

export default StepFooterActions;
